import FormattedMessage from 'I18n/components/FormattedMessage';
import Products from 'merchandise-lib/constants/Product';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import { useSelector } from 'react-redux';
import { FREE } from 'self-service-api/constants/ProductLevels';
import HubProductCTA from '../../../features/purchaseMotions/components/HubProductCTA';
import MerchCardCTAOverridesWrapper from '../../../features/purchaseMotions/components/MerchCardCTAOverridesWrapper';
import { SuiteCTA } from '../../../features/purchaseMotions/components/SuiteCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useHubPurchaseMotions } from '../../../features/purchaseMotions/hooks/useHubPurchaseMotions';
import { useSuitePurchaseMotionsQuery } from '../../../features/purchaseMotions/hooks/useSuitePurchaseMotionsQuery';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { ErrorBoundary } from '../../../v2/components/error-boundary/ErrorBoundary';
import { ErrorBoundaryTrigger } from '../../../v2/components/error-boundary/ErrorBoundaryTrigger';
import { SCREENS } from '../../../v2/constants/tracking/Screens';
import { PRODUCT_PAGE_FEATURE_TABLE_HEADER } from '../../../v2/constants/tracking/TrackingSources';
import { isValidProductLevel, isValidProductLine } from '../../../v2/utils/typeguards';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import { useSkuAdditionalUnits } from '../../merch-card/useSkuAdditionalUnits';
import FeatureTableColTag from '../FeatureTableColTag';
import FeatureTablePriceDisplay from '../FeatureTablePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ProductFeatureTableHeaderCellContent = ({
  productLevel,
  isLoading,
  skuId,
  productLine,
  apiName
}) => {
  const {
    additionalUnitSkuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const purchaseMotions = useHubPurchaseMotions({
    skuId
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header'
  });
  const {
    suitePurchaseMotions,
    error: suitePurchaseMotionsError,
    loading: suitePurchaseMotionsLoading
  } = useSuitePurchaseMotionsQuery({
    productLevel: productLevel,
    location: 'MERCH_CARD',
    skip: productLevel !== 'STARTER'
  });
  const showSuiteBuyNowButton = productLevel === 'STARTER' && (suitePurchaseMotions === null || suitePurchaseMotions === void 0 ? void 0 : suitePurchaseMotions.length) > 0;
  if (skuId && apiName) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlock: "100",
        as: "div",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: isLoading,
          heights: [20],
          widths: [140],
          children: /*#__PURE__*/_jsx(FeatureTablePriceDisplay, {
            apiName: apiName
          })
        })
      }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading || suitePurchaseMotionsLoading,
        heights: [40],
        widths: [140],
        children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
          children: /*#__PURE__*/_jsxs(ErrorBoundary, {
            fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
              apiName: apiName,
              trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
              screen: SCREENS.PRODUCTS_PLANS
            }),
            children: [/*#__PURE__*/_jsx(ErrorBoundaryTrigger, {
              error: suitePurchaseMotionsError
            }), /*#__PURE__*/_jsx(MerchCardCTAOverridesWrapper, Object.assign({
              skuId: skuId,
              trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER
            }, ctaVariantProps, {
              children: showSuiteBuyNowButton ? /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
                purchaseMotion: suitePurchaseMotions[0],
                productLevel: productLevel,
                trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
                screen: SCREENS.PRODUCTS_PLANS,
                purchaseMotionLocation: 'GENERIC' // replace with FEATURE_TABLE when available https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/11974
              }, ctaVariantProps)) : /*#__PURE__*/_jsx(HubProductCTA, Object.assign({
                purchaseMotion: purchaseMotions[0],
                skuId: skuId,
                productLine: productLine,
                additionalUnitSkuId: additionalUnitSkuId,
                trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
                purchaseMotionLocation: 'GENERIC' // replace with FEATURE_TABLE when available https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/11974
              }, ctaVariantProps))
            }))]
          })
        }))
      })]
    });
  }
};
const ProductFeatureTableHeaderCell = ({
  apiName,
  badgeName
}) => {
  const isAppReady = useGlobalLoading();
  const product = useSelector(state => {
    if (!isAppReady) return null;
    return productSelector(state, {
      apiName
    });
  });
  const productLevel = product === null || product === void 0 ? void 0 : product.productLevel;
  const productLine = product === null || product === void 0 ? void 0 : product.productLine;
  const skuIdToUse = product === null || product === void 0 ? void 0 : product.currentSkuId;
  const apiNameForPrice = productLevel === FREE ? Products.HUBSPOT_FREE : apiName;
  const ProductColumnHeaderTitle = () => {
    if (!productLevel || !productLine) return null;
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `compare.productLevel.${productLevel}`
    });
  };
  if (!isValidProductLevel(productLevel) || !isValidProductLine(productLine)) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    paddingInline: "0",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
      apiName: apiName,
      badgeName: badgeName
    }), /*#__PURE__*/_jsx(View, {
      maxInlineSize: "13ch",
      children: /*#__PURE__*/_jsx(Text, {
        as: "strong",
        typography: "heading-50",
        children: /*#__PURE__*/_jsx(ProductColumnHeaderTitle, {})
      })
    }), /*#__PURE__*/_jsx(ProductFeatureTableHeaderCellContent, {
      productLevel: productLevel,
      isLoading: !isAppReady,
      skuId: skuIdToUse,
      productLine: productLine,
      apiName: apiNameForPrice
    })]
  });
};
export default ProductFeatureTableHeaderCell;